import { useMemo } from "react";
import { Controller } from "react-hook-form";
import { NumericFormat } from "react-number-format";
import { useIntl } from "react-intl";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useTranslation } from "react-i18next";
import { cn } from "@/lib/utils";

/**
 * Base props for the FormattedNumberInputField component
 */
interface BaseProps {
  name: string;
  control: any;
  disabled?: boolean;
  isInteger?: boolean;
  currency?: string;
  decimalScale?: number;
  placeholder?: string;
  additionalSuffix?: string;
  additionalPrefix?: string;
  style?: "currency" | "decimal";
  hideLabel?: boolean;
  className?: string;
  spaceBetweenSuffix?: boolean;
  showOptional?: boolean;
  /** Optional callback called when the field loses focus */
  onBlur?: () => void;
};

interface StandalonePropsBase {
  /** Whether to render in standalone mode with label */
  isStandalone?: boolean;
}

interface StandalonePropsWithLabel extends StandalonePropsBase {
  /** Whether to render in standalone mode with label */
  isStandalone: true;
  /** Label text when in standalone mode */
  label: string;
}

interface StandalonePropsWithoutLabel extends StandalonePropsBase {
  /** Whether to render in standalone mode with label */
  isStandalone?: false;
  label?: never;
}

type StandaloneProps = StandalonePropsWithLabel | StandalonePropsWithoutLabel;

/** Combined props type for FormattedNumberInputField */
type FormattedNumberInputFieldProps = BaseProps & StandaloneProps;

/**
 * A number input field component that handles number formatting based on locale
 *
 * Features:
 * - Locale-aware number formatting
 * - Currency symbol handling
 * - Thousands/decimal separators
 * - Optional standalone mode with label
 * - Integration with React Hook Form
 *
 * @param {Object} props - Component props
 * @param {string} props.name - Field name for form control
 * @param {any} props.control - React Hook Form control object
 * @param {boolean} [props.disabled] - Whether the input is disabled (default: false)
 * @param {boolean} [props.isInteger] - Whether to only allow integer values (default: false)
 * @param {string} [props.currency] - Currency code e.g. "EUR", "USD" (default: "EUR")
 * @param {number} [props.decimalScale] - Number of decimal places to show (default: 2)
 * @param {boolean} [props.isStandalone] - Whether to render in standalone mode with label and error message (default: false)
 * @param {string} [props.label] - Label text when in standalone mode (default: undefined)
 * @param {string} [props.placeholder] - Placeholder text (default: "0")
 * @param {string} [props.additionalSuffix] - Additional suffix to append after currency symbol (default: "")
 * @param {string} [props.additionalPrefix] - Additional prefix to prepend before currency symbol (default: "")
 * @param {"currency"|"decimal"} [props.style] - Number formatting style (default: "decimal")
 * @param {boolean} [props.hideLabel] - Whether to not render a label (default: false)
 * @param {string} [props.className] - Additional className for the input field (default: "")
 * @param {boolean} [props.spaceBetweenSuffix] - Whether to add a space between the suffix and the input (default: false)
 * @param {boolean} [props.showOptional] - Whether to show the optional text (default: false)
 * @returns A formatted number input field
 */
function FormattedNumberInputField(props: FormattedNumberInputFieldProps) {
  const {
    name,
    control,
    disabled = false,
    isInteger = false,
    currency = "EUR",
    decimalScale = 2,
    isStandalone = false,
    placeholder = "0",
    additionalSuffix = "",
    additionalPrefix = "",
    style = "decimal",
    hideLabel = false,
    className,
    spaceBetweenSuffix = false,
    showOptional = false,
    onBlur: customOnBlur,
  } = props;

  const intl = useIntl();
  const { t } = useTranslation(["common"]);

  // Extract separators and currency symbol from intl
  const { thousandSeparator, decimalSeparator, currencySymbol, symbolPosition } = useMemo(() => {
    // Hard coded sample value to get the separators and currency symbol
    const formatter = intl.formatNumberToParts(1234567.89, {
      style,
      currency,
    });

    const currencyPart = formatter.find((part) => part.type === "currency");

    // Determine the position of the currency symbol by checking the index of the currency and integer parts
    const symbolPosition = formatter.findIndex((part) => part.type === "currency") >
      formatter.findIndex((part) => part.type === "integer")
      ? "suffix"
      : "prefix";

    return {
      thousandSeparator: formatter.find((part) => part.type === "group")?.value || ",",
      decimalSeparator: formatter.find((part) => part.type === "decimal")?.value || ".",
      currencySymbol: currencyPart?.value || "",
      symbolPosition,
    };
  }, [intl, currency]);

  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState }) => {
        const { onChange, value, onBlur, ref } = field;
        const { error } = fieldState;

        // Ensure the value passed to NumericFormat is always a string (it expects a numeric string when
        // valueIsNumericString is true). This avoids runtime errors if the form value is a number or Date.
        const displayValue =
          value === undefined || value === null || value === ""
            ? ""
            : String(value);

        const inputField = (
          <>
            <NumericFormat
              id={name}
              name={name}
              value={displayValue}
              thousandSeparator={thousandSeparator}
              decimalSeparator={decimalSeparator}
              prefix={symbolPosition === "prefix" ? additionalPrefix + currencySymbol : additionalPrefix}
              suffix={symbolPosition === "suffix" ? currencySymbol + additionalSuffix : spaceBetweenSuffix ? " " + additionalSuffix : additionalSuffix}
              customInput={Input}
              disabled={disabled}
              type="text"
              decimalScale={isInteger ? 0 : decimalScale}
              valueIsNumericString={true}
              getInputRef={ref}
              onValueChange={(values) => {
                onChange(values.floatValue ?? "");
              }}
              onBlur={() => {
                onBlur();
                customOnBlur?.();
              }}
              placeholder={placeholder + (spaceBetweenSuffix ? " " : "") + additionalSuffix}
              className={cn("w-full", className)}
            />
          </>
        );

        if (isStandalone) {
          return (
            <div className={cn("space-y-2 form-item", className)}>
              {!hideLabel && (
                <Label htmlFor={name} className={cn(error && "text-red-500")}>
                  {props.label}
                  {showOptional &&
                    <span className="text-slate-400 font-normal">&nbsp;({t("optional")})</span>
                  }
                </Label>
              )}
              {inputField}
              {error && <p className="input-error-message">{error.message}</p>}
            </div>
          );
        }

        return inputField;
      }}
    />
  );
}

export default FormattedNumberInputField;
